/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useContext } from 'react';
import { DataTable } from '@scuf/datatable';
import { ICellData } from '@scuf/datatable/dist/components/DataTable/IDataTableInterfaces';
import '../MeterSavingsDetailTable/MeterSavingsTableTheme.scss';
import {
  IDataTableColumn,
  IUtilitySitesSavingsDetailTableProps
} from './ISiteSavingsDetailTableProps';
import {
  formatCurrency,
  getAbsoluteValue,
  clsNameCheck,
  savingsBadgeStyleCheck,
  totalConsumptionSavingsCheck,
  scoreCategoryPercentCheck
} from '../Utils/Utils';
import {
  KPI_PERCENTAGE_DECIMAL_PRECISION,
  KPI_VALUE_DECIMAL_PRECISION,
  PERCENT_CHANGE_THRESHOLD,
  UtilityDetailMeterField
} from '../Utils/Constants';
import { ApiContext } from '../../../context/ApiContext';
import { IUtilityCostSavings } from '../SavingsMeter/SavingsMeterProps';
import { renderActualConsumCell } from '../MeterSavingsDetailTable/MeterSavingsDetailTableUtility';
import { renderActualCost } from '../SavingsMeter/SavingMeterWidgetUtils';
import { getConvertedValue } from '../Utils/unitHandling/unitHandling';

const UtilitySitesSavingsDetailTable = (
  props: IUtilitySitesSavingsDetailTableProps
) => {
  const {
    siteData,
    utilityCostCurrency,
    utilityMeasurementUnit,
    columnDetails,
    utilityType
  } = props;

  const apiContext = useContext(ApiContext);
  const { onPortfolioSiteClick, apiCallAt } = apiContext;
  const onSiteclick = (data: IUtilityCostSavings) => {
    if (data && data.siteId) {
      if (onPortfolioSiteClick) {
        onPortfolioSiteClick(data.siteId);
      }
    }
  };

  const clsName = clsNameCheck(apiCallAt);

  const cellDataRenderer = (cellData: ICellData) => {
    const { value, field, rowData } = cellData;
    const currencyconverter = formatCurrency(value, utilityCostCurrency!);

    if (field === UtilityDetailMeterField.totalActualConsumption) {
      return renderActualConsumCell(
        cellData,
        utilityMeasurementUnit,
        props.originalUnit,
        utilityType
      );
    }
    if (field === UtilityDetailMeterField.totalConsumptionSavings) {
      if (
        value === null ||
        (apiContext.showChangeWhenData &&
          (Number(rowData.previousPeriodConsumption) === 0 ||
            Math.abs(rowData.percentageConsumptionSaving) >
              PERCENT_CHANGE_THRESHOLD))
      ) {
        return <span className="score-category-meterdata-meter">--</span>;
      } else {
        return (
          <span className="score-category-meterdata-meter">
            <span
              className="savings-badge"
              style={{
                color: savingsBadgeStyleCheck(value)
              }}
            >
              {getAbsoluteValue(
                Number(
                  getConvertedValue(
                    utilityType,
                    props.originalUnit ? props.originalUnit : 'kilowatt hour',
                    value,
                    rowData.changeUnit
                  )?.Value
                )
              ).toFixed(KPI_VALUE_DECIMAL_PRECISION)}
              {' ' + props.originalUnit
                ? rowData.changeUnit +
                  ' ' +
                  '(' +
                  Math.abs(rowData.percentageConsumptionSaving).toFixed(
                    KPI_PERCENTAGE_DECIMAL_PRECISION
                  ) +
                  '%)'
                : utilityMeasurementUnit +
                  '(' +
                  Math.abs(rowData.percentageConsumptionSaving).toFixed(
                    KPI_PERCENTAGE_DECIMAL_PRECISION
                  ) +
                  '%)'}
            </span>
          </span>
        );
      }
    }
    if (field === UtilityDetailMeterField.totalCostSavings) {
      if (value === null) {
        return <span className="score-category-meterdata-meter">--</span>;
      } else {
        return (
          <div
            style={{ display: 'flex' }}
            className="score-category-meterdata-meter"
          >
            <div
              className="savings-badge"
              style={{
                color: savingsBadgeStyleCheck(value)
              }}
            >
              {currencyconverter}
            </div>
            <div
              className="savings-badge"
              style={{
                color: savingsBadgeStyleCheck(value)
              }}
            >
              {'(' +
                Math.abs(rowData.percentageCostSavings).toFixed(
                  KPI_PERCENTAGE_DECIMAL_PRECISION
                ) +
                '%)'}
            </div>
          </div>
        );
      }
    }
    if (field === UtilityDetailMeterField.siteName) {
      return (
        <span
          className={clsName}
          onClick={() => {
            onSiteclick(rowData);
          }}
        >
          {totalConsumptionSavingsCheck(rowData.totalConsumptionSavings)}
          <span className="score-category-percent">
            {scoreCategoryPercentCheck(value)}
          </span>
        </span>
      );
    }

    if (field === UtilityDetailMeterField.previousPeriodCost) {
      return (
        <span className="score-category-meterdata-meter">
          <span className="score-category-percent utility-prev-cost">
            {currencyconverter}
          </span>
        </span>
      );
    }
    if (field === UtilityDetailMeterField.previousPeriodConsumption) {
      return (
        <span className="score-category-meterdata-meter">
          <div className="consumptionvalue">
            {getConvertedValue(
              utilityType,
              props.originalUnit ? props.originalUnit : 'kilowatt hour',
              value,
              rowData.baselineUnit
            )?.Value?.toFixed(KPI_VALUE_DECIMAL_PRECISION)}
          </div>
          {props.originalUnit ? rowData.baselineUnit : utilityMeasurementUnit}
        </span>
      );
    }
    if (field === UtilityDetailMeterField.totalActualCost) {
      return renderActualCost(rowData, currencyconverter);
    }
    return <></>;
  };

  return (
    <DataTable className="Savingtable" data={siteData}>
      {columnDetails.map((column: IDataTableColumn) => {
        return (
          <DataTable.Column
            key={column.field}
            className={column.className}
            field={column.field}
            header={column.header}
            sortable={column.sortable}
            renderer={cellDataRenderer}
            initialWidth={column.initialWidth}
          />
        );
      })}
    </DataTable>
  );
};
export default UtilitySitesSavingsDetailTable;
